import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { PlusIcon, CheckBadgeIcon } from '@heroicons/react/24/outline';
// I'll reuse EligibilityCard but maybe create a specific one if needed.
// For now, I'll create a local specialized card or a generic one.
import ComplaintMethodCard from '../components/ComplaintMethodCard';
import ComplaintMethodModal from '../components/ComplaintMethodModal';
import ConfirmDialog from '../components/ConfirmDialog';

const GrievanceComplaintMethodsPage = () => {
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingMethod, setEditingMethod] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, methodId: null });

    // Section Settings State
    const [settings, setSettings] = useState({
        section_title: 'How to Raise a Complaint',
        section_description: 'If you have any concerns, queries, or complaints regarding our services, we encourage you to share them with us. You may register your grievance at the branch where your loan was availed.',
        badge_text: 'Level 1',
        footer_text: 'We aim to acknowledge and address your complaint at the branch level within 15 working days.'
    });
    const [savingSettings, setSavingSettings] = useState(false);

    // Fetch data
    const fetchData = async () => {
        try {
            setLoading(true);
            const [methodsRes, settingsRes] = await Promise.all([
                axios.get('/api/admin/grievance-complaint/methods'),
                axios.get('/api/admin/grievance-complaint/methods/settings')
            ]);
            setMethods(methodsRes.data.data || []);
            if (settingsRes.data.success && settingsRes.data.data) {
                setSettings(settingsRes.data.data);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Failed to load data');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    // Handle create/update
    const handleSubmit = async (formData) => {
        try {
            setSubmitting(true);
            if (editingMethod) {
                await axios.put(`/api/admin/grievance-complaint/methods/${editingMethod.id}`, formData);
                toast.success('Method updated successfully');
            } else {
                await axios.post('/api/admin/grievance-complaint/methods', formData);
                toast.success('Method created successfully');
            }
            setIsModalOpen(false);
            setEditingMethod(null);
            fetchData();
        } catch (error) {
            console.error('Error saving method:', error);
            toast.error('Failed to save method');
        } finally {
            setSubmitting(false);
        }
    };

    // Handle delete
    const handleDelete = (id) => {
        setDeleteConfirm({ isOpen: true, methodId: id });
    };

    const confirmDelete = async () => {
        try {
            await axios.delete(`/api/admin/grievance-complaint/methods/${deleteConfirm.methodId}`);
            toast.success('Method deleted successfully');
            setDeleteConfirm({ isOpen: false, methodId: null });
            fetchData();
        } catch (error) {
            console.error('Error deleting method:', error);
            toast.error('Failed to delete method');
        }
    };

    const handleSettingsChange = (e) => {
        const { name, value } = e.target;
        setSettings(prev => ({ ...prev, [name]: value }));
    };

    const saveSettings = async (e) => {
        e.preventDefault();
        try {
            setSavingSettings(true);
            await axios.put('/api/admin/grievance-complaint/methods/settings', settings);
            toast.success('Section settings updated successfully');
        } catch (error) {
            console.error('Error saving settings:', error);
            toast.error('Failed to save section settings');
        } finally {
            setSavingSettings(false);
        }
    };

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold text-slate-800">Branch Level Methods (Level 1)</h1>
                    <p className="text-slate-500 mt-1">Manage ways customers can raise complaints at the branch level</p>
                </div>
                <button
                    onClick={() => {
                        setEditingMethod(null);
                        setIsModalOpen(true);
                    }}
                    className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl"
                >
                    <PlusIcon className="h-5 w-5" />
                    Add New Method
                </button>
            </div>

            {/* Section Settings */}
            <div className="bg-white rounded-3xl p-6 shadow-sm border border-slate-100">
                <h3 className="text-lg font-bold text-slate-800 mb-4 flex items-center gap-2">
                    <CheckBadgeIcon className="h-5 w-5 text-primary-500" />
                    Section Settings
                </h3>
                <form onSubmit={saveSettings} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Badge Text (e.g., Level 1)</label>
                        <input
                            type="text"
                            name="badge_text"
                            value={settings.badge_text}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Level 1"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Heading</label>
                        <input
                            type="text"
                            name="section_title"
                            value={settings.section_title}
                            onChange={handleSettingsChange}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="How to Raise a Complaint"
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Section Description</label>
                        <textarea
                            name="section_description"
                            value={settings.section_description}
                            onChange={handleSettingsChange}
                            rows={3}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Describe the complaint process..."
                        />
                    </div>
                    <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-slate-700 mb-2">Footer Info Text (Resolution Info)</label>
                        <textarea
                            name="footer_text"
                            value={settings.footer_text}
                            onChange={handleSettingsChange}
                            rows={2}
                            className="w-full px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-primary-500"
                            placeholder="Resolution time information..."
                        />
                    </div>
                    <div className="md:col-span-2 flex justify-end">
                        <button
                            type="submit"
                            disabled={savingSettings}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-2 rounded-xl font-semibold transition-all disabled:opacity-50"
                        >
                            {savingSettings ? 'Saving...' : 'Save Section Settings'}
                        </button>
                    </div>
                </form>
            </div>

            {/* Methods Grid */}
            {loading ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="bg-white rounded-2xl h-48 shadow-sm animate-pulse border border-slate-100"></div>
                    ))}
                </div>
            ) : methods.length === 0 ? (
                <div className="bg-white rounded-3xl p-12 text-center shadow-sm border border-slate-100">
                    <div className="max-w-md mx-auto">
                        <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
                            <CheckBadgeIcon className="h-8 w-8 text-slate-400" />
                        </div>
                        <h3 className="text-xl font-bold text-slate-800 mb-2">No methods added yet</h3>
                        <p className="text-slate-500 mb-6">Create complaint methods to showcase on the grievances page.</p>
                        <button
                            onClick={() => {
                                setEditingMethod(null);
                                setIsModalOpen(true);
                            }}
                            className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-lg"
                        >
                            Add First Method
                        </button>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {methods.map((method) => (
                        <ComplaintMethodCard
                            key={method.id}
                            method={method}
                            onEdit={(m) => {
                                setEditingMethod(m);
                                setIsModalOpen(true);
                            }}
                            onDelete={handleDelete}
                        />
                    ))}
                </div>
            )}

            {/* Modal */}
            <ComplaintMethodModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                    setEditingMethod(null);
                }}
                onSubmit={handleSubmit}
                method={editingMethod}
                loading={submitting}
            />

            {/* Confirm Dialog */}
            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, methodId: null })}
                onConfirm={confirmDelete}
                title="Delete Method"
                message="Are you sure you want to delete this complaint method? This action cannot be undone."
            />
        </div>
    );
};

export default GrievanceComplaintMethodsPage;
